import store from "@/store/store";

export default {
    // The method that is used for all AJAX calls
    xhr: function (settings) {
        let type = settings.type || `POST`;
        let xhr = new XMLHttpRequest();
        let data = {};
        let serversBusyTimeout;

        // Don't overwrite and then remove other errors
        if (store.state.global.errorMessage === ``) {
            // Show an error when it takes too long for the server to give back a response
            serversBusyTimeout = setTimeout(() => {
                store.commit(`changeGlobal`, {
                    variableName: `errorMessage`,
                    newValue: `Something went wrong, please try again later`,
                });
            }, store.state.settings.serversBusyTimeoutMs);
        }

        store.commit(`changeGlobal`, {
            variableName: `showLoader`,
            newValue: true,
        });

        xhr.open(type, settings.url);

        xhr.withCredentials = settings.withCredentials;

        if (settings.setContentTypeHeader !== false) xhr.setRequestHeader(`Content-Type`, `application/json`);
        if (settings.setSurveyId === true) xhr.setRequestHeader(`CTSID`, store.state.surveyInfo.surveyId);
        
        if(typeof settings.data === `object`) {
            for (let dataKey in settings.data) {
                data[dataKey] = settings.data[dataKey];
            }
        } else {
            data = settings.data;
        }
 
        xhr.onreadystatechange = () => {
            if (xhr.readyState !== 4) return;
            if (xhr.status === 200) {
                store.commit(`changeGlobal`, {
                    variableName: `errorMessage`,
                    newValue: ``,
                });

                clearTimeout(serversBusyTimeout);
                if (settings.callback) settings.callback(xhr);
            } else if (xhr.status === 401) {
                store.commit(`changeGlobal`, {
                    variableName: `errorMessage`,
                    newValue: store.getters.textResource(`rs_inactivity_text`),
                });
                clearTimeout(serversBusyTimeout);
            } else {
                console.log(`error`, xhr.status, xhr.statusText);
            }
        };
        
        xhr.send(JSON.stringify(data));
    },
    hexToRgb(c) {
        let color = c.substring(1);      // strip #
        let rgb = parseInt(color, 16);   // convert rrggbb to decimal
        let r = (rgb >> 16) & 0xff;  // extract red
        let g = (rgb >> 8) & 0xff;  // extract green
        let b = (rgb >> 0) & 0xff;  // extract blue
        let luma =  (r * 299 + g * 587 + b * 114) / 1000;

        if (luma < 128) {
            // pick a different colour
            return `light`;
        }
        else {
            return `dark`;
        }
    },
    // Returns all the querystrings in the form of an object. Because native solutions don't work in IE
    querystringObj: function (strings) {
        let urlParams;
        urlParams = strings || window.location.search;
        const urlSearchParams = new URLSearchParams(urlParams);
        const params = Object.fromEntries(urlSearchParams.entries());

        return params;
    },
    hyphenate(str) {
        return str
            .split(/(?=[A-Z])/)
            .join(`-`)
            .toLowerCase();
    },
    setFavicon(url) {
        let link = document.querySelector("link[rel*='icon']") || document.createElement(`link`);
        link.type = `image/x-icon`;
        link.rel = `shortcut icon`;
        link.href = url;
        document.getElementsByTagName(`head`)[0].appendChild(link);
    },
    getParameterByName: function (name, url) {
        if (!url) url = window.location.href;
        name = name.replace(/[[\]]/g, `\\$&`);
        let regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return ``;

        return decodeURIComponent(results[2].replace(/\+/g, ` `));
    },
    getCookie: function (name) {
        let match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
        if (match) return match[2];
    },
    removeHTMLFromString: function (str) {
        return str.replace(/(<([^>]+)>)/gi, ``);
    },
    ddmmyyyToDate: function (str) {
        let dateParts = str.split(`-`);

        return new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0]);
    },
    // Converts a page object into a stripped down object so only the relevant properties are sent to the server. This reduces traffic
    toStrippedQuestionObj: function (pageObj) {
        return {
            timeOfPageLoad: pageObj.timeOfPageLoad,
            questions: pageObj.questions.map((question) => {
                // When users can check answers, we should clear all openfields that aren't checked, otherwise the back-end thinks these answers should be checked
                const userCanCheckAnswer = [`Single`, `Multi`].includes(question.type);

                if (question.subQuestions !== undefined && question.subQuestions !== null) {
                    return {
                        id: question.id,
                        type: question.type,
                        subQuestions: question.subQuestions.map((sq) => ({
                            id: sq.id,
                            answers: sq.answers.map((answer) => ({
                                id: answer.id,
                                isChecked: answer.isChecked,
                                openField: answer.openField,
                            })),
                        })),
                    };
                } else {
                    if (question.type === `FileUpload`) {
                        return {
                            id: question.id,
                            answers: question.answers.map((answer) => ({
                                id: answer.id,
                                isChecked: answer.isChecked,
                                openField: answer.openField,
                                uploads: answer.uploads?.map((file) => ({ Id: file.id, }))
                            })),
                        };
                    } else {
                        return {
                            id: question.id,
                            answers: question.answers.map((answer) => ({
                                id: answer.id,
                                isChecked: answer.isChecked,
                                openField: answer.isChecked === false && userCanCheckAnswer === true ? "" : answer.openField
                            })),
                        };
                    }
                }
            }),
        };
    },
    getBrowser: function () {
        var ua = navigator.userAgent,
            tem,
            M = ua.match(/(opera|chrome|safari|firefox|trident(?=\/))\/?\s*(\d+)/i) || [];
        if (/trident/i.test(M[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return { name: "IE", version: tem[1] || "" };
        }
        if (navigator.userAgent.indexOf("Edge/") > -1) {
            return {
                name: "Edge",
            };
        }
        if (M[1] === "Chrome") {
            tem = ua.match(/\b(OPR|Edge)\/(\d+)/);
            if (tem != null)
                return tem
                    .slice(1)
                    .join(" ")
                    .replace("OPR", "Opera");
        }
        M = M[2] ? [M[1], M[2]] : [navigator.appName, navigator.appVersion, "-?"];
        if ((tem = ua.match(/version\/(\d+)/i)) != null) {
            M.splice(1, 1, tem[1]);
        }
        return {
            name: M[0],
            version: M[1],
        };
    }
};
